import { Box, Grid, GridItem, chakra } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import {
  FEATURE_FLAGS,
  PARTIALLY_SUPPORTED_PLATFORMS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { CREATOR_MANAGER_RELATION } from '~/constants/creatorManagerRelation';
import { IGInsights } from '~/hooks/api/insights/useGetIGInsights';
import { TTInsights } from '~/hooks/api/insights/useGetTTInsights';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';
import { useCurrentUserCreatorRelation } from '~/hooks/useCurrentUserCreatorRelation';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import {
  EmptyState,
  SupportedPlatforms,
  getInsightId,
  getIsEmptyData,
  getPlatformStatus,
} from '~/screens/CreatorAudienceDemographics';
import { AgeDistribution } from '~/screens/CreatorAudienceDemographics/AgeDistribution';
import { CountryDistribution } from '~/screens/CreatorAudienceDemographics/CountryDistribution';
import {
  GenderDistribution,
  useInsights,
} from '~/screens/CreatorAudienceDemographics/GenderDistribution';
import { CreatorType } from '~/types';

import { DistributionWrapper } from './DistributionWrapper';
import { PlatformChartsCaption } from './PlatformChartsCaption';

export type SupportedCharts = 'age' | 'gender' | 'country';

interface Props {
  platform: SupportedPlatforms;
  demographics?: SupportedCharts[];
  creatorSlug?: string;
  isBuilder?: boolean;
  isWhalarModeUser?: boolean;
  isPubliclyVisible?: boolean;
}

export const PlatformCharts = ({
  platform,
  demographics,
  creatorSlug,
  isBuilder = false,
}: Props) => {
  const foamSmartTalentMetricsFeatureFlag = useFeature(
    FEATURE_FLAGS.foamSmartTalentMetrics,
  );

  const router = useRouter();
  const slug = router.query.creatorSlug as string;

  const creator = useGetCreatorProfile({ slug: creatorSlug || slug });
  const insightId =
    getInsightId({
      creator: creator.data,
      platform,
    }) || '';

  const connectionStatus = getPlatformStatus({
    creator: creator.data,
    platform,
  });

  const isConnected = match(platform)
    .with(
      PARTIALLY_SUPPORTED_PLATFORMS.snapchat,
      () =>
        connectionStatus === 'connected' ||
        connectionStatus === 'disconnected' ||
        connectionStatus === 'off-platform',
    )
    .otherwise(
      () =>
        connectionStatus === 'connected' || connectionStatus === 'disconnected',
    );

  const insights = useInsights({ insightId, platform });

  const relationToCreator = useCurrentUserCreatorRelation(
    creator.data as CreatorType,
  );

  const isManager =
    relationToCreator === CREATOR_MANAGER_RELATION.coManager ||
    relationToCreator === CREATOR_MANAGER_RELATION.manager;

  const isEmpty = getIsEmptyData(insights?.data);

  let demographicsSource;

  if (platform === SOCIAL_PLATFORMS.youtube) {
    demographicsSource = creator.data?.ytChannels[0]?.demographicsSource;
  } else {
    demographicsSource = (insights?.data as IGInsights | TTInsights)
      ?.demographicsSource;
  }

  const isSTMPrediction = demographicsSource === 'stm-predictions';
  const isPredictedVisible =
    connectionStatus === 'off-platform' &&
    isSTMPrediction &&
    foamSmartTalentMetricsFeatureFlag.isEnabled;

  const showEmptyState = useMemo(
    () =>
      (!isConnected && !isPredictedVisible) || (isEmpty && !isPredictedVisible),
    [isConnected, isEmpty, isPredictedVisible],
  );

  return (
    <chakra.section>
      <Box position="relative">
        <Grid
          templateColumns="repeat(auto-fit, minmax(360px, 1fr))"
          gap={6}
          alignItems="stretch"
        >
          {(!isBuilder || demographics?.includes('gender')) && (
            <GridItem>
              <DistributionWrapper>
                <GenderDistribution
                  insightId={insightId}
                  platform={platform}
                  isConnected={isConnected || isPredictedVisible}
                />
              </DistributionWrapper>
            </GridItem>
          )}

          {(!isBuilder || demographics?.includes('age')) && (
            <GridItem>
              <DistributionWrapper>
                <AgeDistribution
                  insightId={insightId}
                  platform={platform}
                  isConnected={isConnected || isPredictedVisible}
                />
              </DistributionWrapper>
            </GridItem>
          )}

          {(!isBuilder || demographics?.includes('country')) && (
            <GridItem>
              <DistributionWrapper>
                <CountryDistribution
                  insightId={insightId}
                  platform={platform}
                  isConnected={isConnected}
                  isPredictedVisible={isPredictedVisible}
                  creator={creator.data}
                />
              </DistributionWrapper>
            </GridItem>
          )}
        </Grid>

        {showEmptyState ? (
          <EmptyState
            isEmpty={isEmpty}
            isConnected={isConnected}
            isManager={isManager}
          />
        ) : null}
      </Box>

      <PlatformChartsCaption
        creator={creator.data}
        insights={insights?.data}
        platform={platform}
        connectionStatus={connectionStatus}
      />
    </chakra.section>
  );
};
